<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-50 overflow-y-auto"
    aria-labelledby="modal-title"
    role="dialog"
    aria-modal="true"
  >
    <div
      class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
    >
      <!-- Background overlay -->
      <div
        class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        aria-hidden="true"
        @click="closeModal"
      />

      <!-- Modal panel -->
      <div
        class="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6"
      >
        <div>
          <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
            <PhoneIcon class="h-6 w-6 text-green-600" aria-hidden="true" />
          </div>

          <div class="mt-3 text-center sm:mt-5">
            <h3 id="modal-title" class="text-lg leading-6 font-medium text-gray-900">
              Phone Verification
            </h3>

            <!-- Step 1: Enter Phone Number -->
            <div v-if="step === 'phone'" class="mt-2">
              <p class="text-sm text-gray-500 mb-4">
                Enter your phone number to receive a verification code
              </p>

              <div class="mt-4 space-y-3">
                <!-- Country Code Selector -->
                <div class="flex">
                  <select
                    v-model="selectedCountry"
                    class="rounded-l-md border-gray-300 border-r-0 focus:ring-green-500 focus:border-green-500 text-sm"
                    :disabled="loading"
                  >
                    <option v-for="country in countries" :key="country.code" :value="country">
                      {{ country.flag }} {{ country.name }} ({{ country.dialCode }})
                    </option>
                  </select>

                  <input
                    id="phone"
                    v-model="phoneNumber"
                    type="tel"
                    name="phone"
                    class="flex-1 shadow-sm focus:ring-green-500 focus:border-green-500 block w-full sm:text-sm border-gray-300 rounded-r-md"
                    :placeholder="selectedCountry.placeholder"
                    :disabled="loading"
                    @keyup.enter="sendVerificationCode"
                    @input="formatPhoneNumber"
                  />
                </div>

                <div v-if="formattedPhone" class="text-sm text-gray-600">
                  Full number: {{ formattedPhone }}
                </div>
              </div>

              <div v-if="error" class="mt-3 text-sm text-red-600">
                {{ error }}
              </div>
            </div>

            <!-- Step 2: Enter Verification Code -->
            <div v-if="step === 'verify'" class="mt-2">
              <p class="text-sm text-gray-500 mb-4">
                We've sent a verification code to<br />
                <strong>{{ maskedPhone }}</strong>
              </p>

              <div class="mt-4">
                <label for="code" class="sr-only">Verification Code</label>
                <input
                  id="code"
                  v-model="verificationCode"
                  type="text"
                  name="code"
                  class="shadow-sm focus:ring-green-500 focus:border-green-500 block w-full sm:text-sm border-gray-300 rounded-md text-center text-lg tracking-widest"
                  placeholder="Enter 6-digit code"
                  maxlength="6"
                  :disabled="loading"
                  @keyup.enter="verifyCode"
                  @input="formatCode"
                />
              </div>

              <div v-if="error" class="mt-3 text-sm text-red-600">
                {{ error }}
              </div>

              <div
                v-if="remainingAttempts !== null && remainingAttempts < 3"
                class="mt-2 text-sm text-orange-600"
              >
                {{ remainingAttempts }} attempts remaining
              </div>

              <div v-if="expiresAt" class="mt-2 text-xs text-gray-500">
                Code expires in {{ timeRemaining }}
              </div>

              <div class="mt-4">
                <button
                  type="button"
                  class="text-sm text-green-600 hover:text-green-500 disabled:text-gray-400"
                  :disabled="loading || !canResend"
                  @click="resendCode"
                >
                  Didn't receive the code? Resend
                </button>
              </div>
            </div>

            <!-- Success State -->
            <div v-if="step === 'success'" class="mt-2">
              <div
                class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4"
              >
                <CheckIcon class="h-6 w-6 text-green-600" aria-hidden="true" />
              </div>
              <p class="text-sm text-gray-500">Your phone number has been successfully verified!</p>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
          <button
            v-if="step === 'phone'"
            type="button"
            class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:col-start-2 sm:text-sm disabled:bg-gray-300 disabled:cursor-not-allowed"
            :disabled="loading || !phoneNumber || !isValidPhoneNumber"
            @click="sendVerificationCode"
          >
            <span v-if="loading" class="flex items-center">
              <svg
                class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                />
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Sending...
            </span>
            <span v-else>Send Code</span>
          </button>

          <button
            v-if="step === 'verify'"
            type="button"
            class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:col-start-2 sm:text-sm disabled:bg-gray-300 disabled:cursor-not-allowed"
            :disabled="loading || !verificationCode || verificationCode.length !== 6"
            @click="verifyCode"
          >
            <span v-if="loading" class="flex items-center">
              <svg
                class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                />
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Verifying...
            </span>
            <span v-else>Verify</span>
          </button>

          <button
            v-if="step === 'success'"
            type="button"
            class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:col-start-2 sm:text-sm"
            @click="closeModal"
          >
            Done
          </button>

          <button
            type="button"
            class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:mt-0 sm:col-start-1 sm:text-sm"
            @click="closeModal"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onUnmounted, onMounted } from 'vue';
import { PhoneIcon, CheckIcon } from '@heroicons/vue/24/outline';
import { storageService } from '../services/storage';
import tierApi from '../services/tierApi';
import { useTierAuthStore } from '@/stores/tierAuth';

interface Props {
  isOpen: boolean;
}

interface Emits {
  (e: 'close'): void;
  (e: 'verified', phoneNumber: string): void;
}

interface Country {
  code: string;
  name: string;
  dialCode: string;
  flag: string;
  placeholder: string;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

// Store instances
const tierAuthStore = useTierAuthStore();

// Country data
const countries: Country[] = [
  { code: 'US', name: 'United States', dialCode: '+1', flag: '🇺🇸', placeholder: '(555) 123-4567' },
  { code: 'GB', name: 'United Kingdom', dialCode: '+44', flag: '🇬🇧', placeholder: '07123 456789' },
  { code: 'CA', name: 'Canada', dialCode: '+1', flag: '🇨🇦', placeholder: '(555) 123-4567' },
  { code: 'AU', name: 'Australia', dialCode: '+61', flag: '🇦🇺', placeholder: '04 1234 5678' },
  { code: 'DE', name: 'Germany', dialCode: '+49', flag: '🇩🇪', placeholder: '015 12345678' },
  { code: 'FR', name: 'France', dialCode: '+33', flag: '🇫🇷', placeholder: '06 12 34 56 78' },
  { code: 'ES', name: 'Spain', dialCode: '+34', flag: '🇪🇸', placeholder: '612 34 56 78' },
  { code: 'IT', name: 'Italy', dialCode: '+39', flag: '🇮🇹', placeholder: '312 345 6789' },
  { code: 'NL', name: 'Netherlands', dialCode: '+31', flag: '🇳🇱', placeholder: '06 12345678' },
  { code: 'BR', name: 'Brazil', dialCode: '+55', flag: '🇧🇷', placeholder: '(11) 91234-5678' },
  { code: 'IN', name: 'India', dialCode: '+91', flag: '🇮🇳', placeholder: '98765 43210' },
  { code: 'CN', name: 'China', dialCode: '+86', flag: '🇨🇳', placeholder: '138 0013 8000' },
  { code: 'JP', name: 'Japan', dialCode: '+81', flag: '🇯🇵', placeholder: '090-1234-5678' },
  { code: 'KR', name: 'South Korea', dialCode: '+82', flag: '🇰🇷', placeholder: '010-1234-5678' },
];

// State
const step = ref<'phone' | 'verify' | 'success'>('phone');
const selectedCountry = ref<Country>(countries[0]);
const phoneNumber = ref('');
const verificationCode = ref('');
const verificationId = ref('');
const maskedPhone = ref('');
const loading = ref(false);
const error = ref('');
const expiresAt = ref<Date | null>(null);
const remainingAttempts = ref<number | null>(null);
const canResend = ref(true);

// Computed
const formattedPhone = computed(() => {
  if (!phoneNumber.value) return '';

  // Remove any non-digit characters
  const cleanPhone = phoneNumber.value.replace(/\D/g, '');

  if (!cleanPhone) return '';

  return `${selectedCountry.value.dialCode}${cleanPhone}`;
});

const isValidPhoneNumber = computed(() => {
  const cleanPhone = phoneNumber.value.replace(/\D/g, '');

  // Basic validation - at least 7 digits, no more than 15
  return cleanPhone.length >= 7 && cleanPhone.length <= 15;
});

const timeRemaining = computed(() => {
  if (!expiresAt.value) return '';

  const now = new Date();
  const diff = expiresAt.value.getTime() - now.getTime();

  if (diff <= 0) return 'Expired';

  const minutes = Math.floor(diff / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);

  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
});

// Timer for expiration
let expirationTimer: NodeJS.Timeout | null = null;

// Methods
const formatPhoneNumber = (event: Event) => {
  const target = event.target as HTMLInputElement;
  let value = target.value.replace(/\D/g, '');

  // Format based on country (simplified US formatting)
  if (selectedCountry.value.code === 'US' || selectedCountry.value.code === 'CA') {
    if (value.length >= 6) {
      value = value.replace(/(\d{3})(\d{3})(\d{0,4})/, '($1) $2-$3');
    } else if (value.length >= 3) {
      value = value.replace(/(\d{3})(\d{0,3})/, '($1) $2');
    }
  }

  target.value = value;
  phoneNumber.value = value;
};

const sendVerificationCode = async () => {
  if (!phoneNumber.value || !isValidPhoneNumber.value) return;

  loading.value = true;
  error.value = '';

  try {
    // Ensure tierApi has the auth token
    const authToken = storageService.getAuthToken();
    if (authToken) {
      tierApi.setToken(authToken);
    }
    const response = await tierApi.verifyPhone({
      action: 'send',
      phoneNumber: formattedPhone.value,
      countryCode: selectedCountry.value.dialCode,
    });

    if (response.success) {
      step.value = 'verify';
      verificationId.value = response.verificationId || '';
      maskedPhone.value = response.maskedPhone || '';
      expiresAt.value = new Date(response.expiresAt || Date.now() + 300000);
      remainingAttempts.value = null;
      startExpirationTimer();
    } else {
      error.value = response.error || 'Failed to send verification code';
    }
  } catch (err) {
    error.value = 'Failed to send verification code. Please try again.';
  } finally {
    loading.value = false;
  }
};

const verifyCode = async () => {
  if (!verificationCode.value || verificationCode.value.length !== 6) return;

  loading.value = true;
  error.value = '';

  try {
    // Ensure tierApi has the auth token
    const authToken = storageService.getAuthToken();
    if (authToken) {
      tierApi.setToken(authToken);
    }

    const response = await tierApi.verifyPhone({
      action: 'verify',
      code: verificationCode.value,
      verificationId: verificationId.value,
    });

    if (response.success) {
      step.value = 'success';
      clearExpirationTimer();

      // Update user verification status in stores
      await tierAuthStore.updateUser({ phoneVerified: true });

      emit('verified', formattedPhone.value);

      // Auto close after 2 seconds
      setTimeout(() => {
        closeModal();
      }, 2000);
    } else {
      error.value = response.error || 'Invalid verification code';
      remainingAttempts.value = (response.maxAttempts || 0) - (response.attempts || 0);
    }
  } catch (err) {
    error.value = 'Verification failed. Please try again.';
  } finally {
    loading.value = false;
  }
};

const resendCode = async () => {
  canResend.value = false;
  await sendVerificationCode();

  // Prevent resend for 60 seconds (SMS has longer delay)
  setTimeout(() => {
    canResend.value = true;
  }, 60000);
};

const formatCode = (event: Event) => {
  const target = event.target as HTMLInputElement;
  // Only allow digits for SMS codes
  target.value = target.value.replace(/\D/g, '');
  verificationCode.value = target.value;
};

const closeModal = () => {
  // Reset state
  step.value = 'phone';
  selectedCountry.value = countries[0];
  phoneNumber.value = '';
  verificationCode.value = '';
  verificationId.value = '';
  maskedPhone.value = '';
  error.value = '';
  expiresAt.value = null;
  remainingAttempts.value = null;
  canResend.value = true;
  clearExpirationTimer();

  emit('close');
};

const startExpirationTimer = () => {
  clearExpirationTimer();

  if (expiresAt.value) {
    const timeUntilExpiry = expiresAt.value.getTime() - Date.now();

    if (timeUntilExpiry > 0) {
      expirationTimer = setTimeout(() => {
        error.value = 'Verification code has expired. Please request a new one.';
        step.value = 'phone';
      }, timeUntilExpiry);
    }
  }
};

const clearExpirationTimer = () => {
  if (expirationTimer) {
    clearTimeout(expirationTimer);
    expirationTimer = null;
  }
};

// Watch for modal close
watch(
  () => props.isOpen,
  newValue => {
    if (!newValue) {
      clearExpirationTimer();
    }
  }
);

// Initialize auth token on mount
onMounted(() => {
  const authToken = storageService.getAuthToken();
  if (authToken) {
    tierApi.setToken(authToken);
  }
});

// Cleanup on unmount
onUnmounted(() => {
  clearExpirationTimer();
});
</script>
